/**
 * playbackEngine – app-weit EIN geteilter Audio-Player für alle osmd-extended `PlaybackManager`.
 *
 * Warum ein Singleton (kritisch – nicht auf „Player pro Vorschau" rückbauen):
 * - `BasicAudioPlayer` hält den SoundFont-Cache (`memoryLoadedSoundFonts`) als **Instanzfeld** und
 *   legt im Konstruktor einen eigenen `AudioContext` an, der nie geschlossen wird (`close()` ist ein
 *   No-op). Jede `MusicXmlPreview` wird bei jedem Tab-Wechsel/Upload/Konvertieren neu gemountet –
 *   ein Player pro Vorschau lud die SoundFonts (gleitz-CDN, MB-groß, 88× `decodeAudioData`) jedes
 *   Mal neu und leckte pro Mount einen AudioContext (Chrome deckelt die Anzahl pro Tab → irgendwann
 *   wirft `new AudioContext()` und die Wiedergabe bleibt still).
 * - `PlaybackManager.initialize()` feuert `open()`/`setSound()` **un-awaited**; `playSound()` wirft
 *   bis zum fertigen Load (still verschluckt) → Noten fallen weg. Deshalb macht die Engine die
 *   Bereitschaft beobachtbar (`subscribe`/`getState`/`isReady`) und `useOsmdPlayback.play()` wartet
 *   darauf.
 * - Das Original-`open()` lädt das Klavier zusätzlich in ein privates, nie genutztes `piano`-Feld –
 *   ein zweiter Download pro Player. Die Überschreibung sät den Slot aus dem Cache.
 *
 * Reines TypeScript (keine Vue-Reaktivität); die reaktive Fassade ist `stores/playbackStore.ts`.
 */
import {BasicAudioPlayer, MidiInstrument, type MusicSheet} from 'opensheetmusicdisplay';
import {playPreviewTone} from '@/composables/osmd/notePreviewTone';

/** SoundFonts, die `PlaybackManager.initialize()` immer anfordert (Melodie-Default, Drums, Metronom). */
export const CORE_SOUNDFONTS: readonly MidiInstrument[] = [
    MidiInstrument.Acoustic_Grand_Piano,
    MidiInstrument.Percussion,
    MidiInstrument.Woodblock,
];

/** Core-Set plus die tatsächlichen Instrumente des geladenen Sheets (dedupliziert, ohne `None`). */
export function requiredSoundFontsFor(sheet: Pick<MusicSheet, 'Instruments'>): MidiInstrument[] {
    const ids = new Set<MidiInstrument>(CORE_SOUNDFONTS);
    for (const instrument of sheet.Instruments) {
        const id = instrument.MidiInstrumentId;
        if (id !== undefined && id !== MidiInstrument.None) ids.add(id);
    }
    return [...ids];
}

export interface SoundFontState {
    loaded: ReadonlySet<MidiInstrument>;
    loading: ReadonlySet<MidiInstrument>;
    /** Instrument → Fehlermeldung des letzten fehlgeschlagenen Loads (Retry über erneutes `preload`). */
    failed: ReadonlyMap<MidiInstrument, string>;
}

type LoadedPlayer = Awaited<ReturnType<BasicAudioPlayer['loadSoundFont']>>;

/** Präziser Zugriff auf das private `piano`-Feld des `BasicAudioPlayer` (siehe Kopfkommentar). */
interface AudioPlayerPrivateCarrier {
    piano?: unknown;
}

export class SharedAudioPlayer extends BasicAudioPlayer {
    private readonly inflight = new Map<MidiInstrument, Promise<LoadedPlayer>>();
    private readonly loaded = new Set<MidiInstrument>();
    private readonly failed = new Map<MidiInstrument, string>();
    private readonly subscribers = new Set<(state: SoundFontState) => void>();

    /** Einziger Lade-Trichter (auch `setSound()` des PlaybackManagers läuft hier durch). */
    override loadSoundFont(soundId: MidiInstrument): Promise<LoadedPlayer> {
        const running = this.inflight.get(soundId);
        if (running) return running;
        const load = super.loadSoundFont(soundId).then(
            (player) => {
                this.loaded.add(soundId);
                this.failed.delete(soundId);
                return player;
            },
            (error: unknown) => {
                this.failed.set(soundId, error instanceof Error ? error.message : String(error));
                throw error;
            },
        ).finally(() => {
            this.inflight.delete(soundId);
            this.emit();
        });
        this.inflight.set(soundId, load);
        this.emit();
        return load;
    }

    /**
     * Sät das private `piano`-Feld aus dem Cache, bevor das Original-`open()` läuft – das lädt sonst
     * das Klavier ein zweites Mal (in den nie genutzten Slot) und resettet erst danach die
     * Kanal-Lautstärken.
     */
    override async open(uniqueInstruments: number[], numberOfInstruments = 16): Promise<void> {
        const carrier = this as unknown as AudioPlayerPrivateCarrier;
        if (carrier.piano === undefined) {
            carrier.piano = await this.loadSoundFont(MidiInstrument.Acoustic_Grand_Piano);
        }
        return super.open(uniqueInstruments, numberOfInstruments);
    }

    /** Lädt alle angegebenen SoundFonts (Fehler landen in `failed`, werfen nicht). */
    async preload(soundIds: readonly MidiInstrument[]): Promise<void> {
        await Promise.allSettled(soundIds.map((id) => this.loadSoundFont(id)));
    }

    isReady(soundIds: readonly MidiInstrument[]): boolean {
        return soundIds.every((id) => this.loaded.has(id));
    }

    getState(): SoundFontState {
        return {
            loaded: new Set(this.loaded),
            loading: new Set(this.inflight.keys()),
            failed: new Map(this.failed),
        };
    }

    subscribe(callback: (state: SoundFontState) => void): () => void {
        this.subscribers.add(callback);
        return () => {
            this.subscribers.delete(callback);
        };
    }

    /** AudioContext in der User-Geste fortsetzen (Autoplay-Policy); No-op, wenn er schon läuft. */
    async resume(): Promise<void> {
        if (this.ac.state !== 'running') await this.ac.resume();
    }

    /**
     * Kurzer Orientierungston für die angegebenen MIDI-Tonhöhen (Vorschau beim Bearbeiten/Auswählen).
     *
     * Bewusst ein **Oszillator** statt `playSound()`: der SoundFont-Weg würde das MB-große
     * Klavier-Sample nachladen (ein bloßer Notenklick stieße den Download an) und bliebe bis dahin
     * stumm. Hier wird nur der ohnehin vorhandene `AudioContext` mitbenutzt – die Synth-Logik liegt
     * in `notePreviewTone.ts`.
     */
    async previewPitches(midiKeys: readonly number[]): Promise<void> {
        if (!midiKeys.length) return;
        await this.resume();
        playPreviewTone(this.ac, midiKeys);
    }

    private emit(): void {
        const state = this.getState();
        for (const callback of this.subscribers) callback(state);
    }
}

let sharedPlayer: SharedAudioPlayer | null = null;

/** Der eine Audio-Player der App (lazy; wird nie disposed). */
export function getSharedAudioPlayer(): SharedAudioPlayer {
    if (!sharedPlayer) sharedPlayer = new SharedAudioPlayer();
    return sharedPlayer;
}

export function resetPlaybackEngineForTests(): void {
    sharedPlayer = null;
}
